import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, Crown, Star, Building2, Zap, Target, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

// Plan icons and colors
const planConfig = {
  premium: {
    icon: Crown,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-200',
    price: '€9,99/mese (IVA incl.)',
    features: ['Archivio Documenti', 'Kit Soci', 'Certificati Base', 'Comunicazioni Email']
  },
  governance: {
    icon: Building2,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-100',
    borderColor: 'border-indigo-200',
    price: '€14,99/mese (IVA incl.)',
    features: ['Rendicontazione Economica', 'Organi Sociali', 'Assemblee', 'Comunicazioni Gruppo']
  },
  pro: {
    icon: Zap,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-200',
    price: '€24,99/mese (IVA incl.)',
    features: ['120+ Categorie Economiche', 'Statistiche Avanzate', 'Import Massivo', 'Chatbot AI']
  },
  enterprise: {
    icon: Target,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-100',
    borderColor: 'border-emerald-200',
    price: '€49,99/mese (IVA incl.)',
    features: ['Volontari Illimitati', 'API Access', 'SSO', 'Supporto Dedicato']
  },
  convenzioni: {
    icon: Users,
    color: 'text-rose-500',
    bgColor: 'bg-rose-100',
    borderColor: 'border-rose-200',
    price: 'Prezzo Custom',
    features: ['Multi-Ente', 'Dashboard Federazione', 'Report Aggregati', 'Account Manager']
  }
};

// Display names for plans
const planDisplayNames = {
  free: 'Free',
  premium: 'Premium',
  governance: 'Governance',
  pro: 'Pro',
  enterprise: 'Enterprise',
  convenzioni: 'Convenzioni Nazionali'
};

export default function UpgradeModal({ 
  open, 
  onClose, 
  requiredPlan, 
  featureName,
  currentPlan = 'free'
}) {
  const navigate = useNavigate();
  
  const config = planConfig[requiredPlan] || planConfig.premium;
  const PlanIcon = config.icon;
  const planName = planDisplayNames[requiredPlan] || requiredPlan?.toUpperCase();
  const currentPlanName = planDisplayNames[currentPlan] || currentPlan?.toUpperCase();
  
  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <div className={`mx-auto p-4 rounded-full ${config.bgColor} ${config.borderColor} border-2 mb-4`}>
            <Lock className={`w-8 h-8 ${config.color}`} />
          </div>
          <DialogTitle className="text-center text-xl">
            Funzionalità riservata al Piano {planName}
          </DialogTitle>
          <DialogDescription className="text-center">
            <span className="font-medium text-slate-700">{featureName}</span> non è disponibile nel tuo piano attuale ({currentPlanName}).
          </DialogDescription>
        </DialogHeader>
        
        <div className={`p-4 rounded-lg ${config.bgColor} ${config.borderColor} border mt-4`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-full bg-white ${config.borderColor} border`}>
              <PlanIcon className={`w-5 h-5 ${config.color}`} />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800">Piano {planName}</h4>
              <p className={`text-sm ${config.color} font-medium`}>{config.price}</p>
            </div>
          </div>
          
          <p className="text-sm text-slate-600 mb-3">
            Con il piano {planName} avrai accesso a:
          </p>
          
          <ul className="space-y-2">
            {config.features.map((feature, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                <div className={`w-1.5 h-1.5 rounded-full ${config.color.replace('text-', 'bg-')}`} />
                {feature}
              </li>
            ))}
          </ul>
        </div>
        
        <div className="flex flex-col gap-3 mt-6">
          <Button 
            onClick={handleUpgrade}
            className={`w-full ${config.color.replace('text-', 'bg-').replace('-500', '-600')} hover:${config.color.replace('text-', 'bg-').replace('-500', '-700')} text-white`}
            data-testid="upgrade-button"
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            Scopri il Piano {planName}
          </Button>
          <Button 
            variant="outline" 
            onClick={onClose}
            className="w-full"
          >
            Resta con il piano attuale
          </Button>
        </div>
        
        <p className="text-xs text-center text-slate-500 mt-2">
          Puoi fare upgrade o downgrade in qualsiasi momento.
        </p>
      </DialogContent>
    </Dialog>
  );
}
